import { z } from 'zod';
/**
 * Доступ по SSH: параметры узла, зондирование, сессия (§7.6, фаза 9b).
 *
 * Протокол здесь не описывается — им занимается `ssh2` в шлюзе. Тут только
 * то, что видят обе стороны: настройки узла, результат зондирования,
 * возможности сессии и управляющие сообщения терминала.
 */
/** Способы аутентификации, которые платформа умеет предъявить серверу. */
export const SSH_AUTH_METHODS = ['password', 'key', 'keyboard_interactive'];
export const sshAuthMethodSchema = z.enum(SSH_AUTH_METHODS);
export const SSH_AUTH_METHOD_KEYS = {
    password: 'ssh.authMethod.password',
    key: 'ssh.authMethod.key',
    keyboard_interactive: 'ssh.authMethod.keyboardInteractive',
};
/** Как шлюз дотянулся до сервера: через агента площадки или напрямую. */
export const sshRouteModeSchema = z.enum(['agent', 'direct']);
/**
 * Значение `TERM` для псевдотерминала.
 *
 * Список закрытый: от `TERM` зависит, какие управляющие последовательности
 * шлёт удалённая программа, и произвольная строка отсюда доехала бы
 * до чужой машины без всякой проверки. `xterm-256color` подходит всему,
 * что новее двухтысячных; `vt100` остаётся для оборудования, которое
 * ничего другого не знает.
 */
export const SSH_TERMS = ['xterm-256color', 'xterm', 'vt100'];
export const sshTermSchema = z.enum(SSH_TERMS);
// — Параметры подключения к узлу ——————————————————————————————
export const nodeSshSchema = z.object({
    nodeId: z.uuid(),
    port: z.number().int(),
    authMethod: sshAuthMethodSchema,
    allowLegacyAlgorithms: z.boolean(),
    term: sshTermSchema,
    /** Закреплённый ключ хоста. NULL — узла ещё не касались (TOFU, ADR 026). */
    hostKeyType: z.string().nullable(),
    hostKeyFp: z.string().nullable(),
    hostKeySeenAt: z.iso.datetime().nullable(),
    banner: z.string().nullable(),
    authMethods: z.array(z.string()).nullable(),
    /** NULL — не зондировали. Отличается от `false` намеренно, как у VNC. */
    sftpSupported: z.boolean().nullable(),
    lastProbeAt: z.iso.datetime().nullable(),
    lastProbeError: z.string().nullable(),
});
/**
 * Имени пользователя здесь нет намеренно.
 *
 * Оно живёт в учётных данных — там же, где пароль или ключ, которым
 * этот пользователь входит. Пока поле было и на узле, и в записи,
 * одно значение лежало в двух местах: смотреть надо было в оба,
 * а разъезжались они молча (замечание заказчика 29.08.2026).
 *
 * Узлу с другим пользователем заводится своя запись учётных данных:
 * пара «кто» и «чем» неразделима — пароль admin не подходит
 * пользователю root, и хранить их порознь смысла нет.
 */
export const updateNodeSshSchema = z
    .object({
    port: z.number().int().min(1).max(65535),
    authMethod: sshAuthMethodSchema,
    allowLegacyAlgorithms: z.boolean(),
    term: sshTermSchema,
})
    .partial();
// — Зондирование ——————————————————————————————————————————
/**
 * Что удалось узнать о сервере, не аутентифицируясь.
 *
 * Зондирование доходит до перечня способов аутентификации и закрывает
 * соединение — то же место, где останавливается зондирование VNC.
 * Учётные данные для него не нужны: проверить, тот ли это порт и та ли
 * машина, должно быть можно до того, как заведён пароль.
 *
 * Ключ хоста при зондировании читается, но **не закрепляется**: закрепление
 * — следствие сознательного подключения, а не побочный итог проверки порта.
 * Иначе достаточно было бы зондирования в неудачный момент, чтобы
 * запомнить ключ подменённой машины.
 */
export const sshProbeResultSchema = z.object({
    /** Дошли ли до TCP-соединения. Всё остальное имеет смысл только при `true`. */
    reachable: z.boolean(),
    /** Идентификация сервера: `SSH-2.0-OpenSSH_9.6`. */
    banner: z.string().nullable(),
    hostKeyType: z.string().nullable(),
    hostKeyFp: z.string().nullable(),
    /**
     * Ключ хоста отличается от закреплённого.
     *
     * Отдельный признак, а не ошибка: зондирование обязано договорить
     * до конца и показать оба отпечатка, иначе подтвердить смену будет нечем.
     */
    hostKeyChanged: z.boolean(),
    /** Способы, которые назвал сервер: `password`, `publickey`, … */
    authMethods: z.array(z.string()),
    /**
     * NULL — не проверяли, и это обычный исход.
     *
     * Спросить сервер про subsystem `sftp` можно только после входа, а вход —
     * это уже не зондирование: у VNC проверка возможностей обходится без
     * пароля (§5.0), у SSH так не выйдет. Поэтому поддержку SFTP выясняет
     * первая же сессия и записывает в `node_ssh`, а зондирование отвечает
     * на вопросы, ответы на которые видны до аутентификации.
     */
    sftpSupported: z.boolean().nullable(),
    route: sshRouteModeSchema.nullable(),
    error: z.string().nullable(),
    errorCode: z.string().nullable(),
    at: z.iso.datetime(),
});
/**
 * Подтверждение смены ключа хоста (§11, фаза 9b).
 *
 * Отпечаток передаётся явно, а не подразумевается: между показом
 * расхождения и нажатием кнопки сервер мог смениться ещё раз, и подтвердить
 * пользователь должен ровно тот ключ, который видел.
 */
export const trustHostKeySchema = z.object({
    fingerprint: z.string().min(1).max(200),
});
// — Сессия ————————————————————————————————————————————————
/**
 * Возможности, обнаруженные при установке соединения.
 *
 * Как и у VNC, интерфейс строится по ним: файловая панель не показывается,
 * если сервер не объявил subsystem `sftp`.
 */
export const sshCapabilitiesSchema = z.object({
    sftp: z.boolean(),
    /** Умеет ли сервер менять размер псевдотерминала на ходу. */
    windowChange: z.boolean(),
});
/** Сведения о сервере для диагностической панели. */
export const sshServerInfoSchema = z.object({
    banner: z.string().nullable(),
    hostKeyType: z.string().nullable(),
    hostKeyFp: z.string().nullable(),
    /** Согласованные алгоритмы: обмен ключами, шифр, MAC. */
    kexAlgorithm: z.string().nullable(),
    cipher: z.string().nullable(),
    mac: z.string().nullable(),
    authMethod: sshAuthMethodSchema,
    legacyAlgorithmsAllowed: z.boolean(),
    route: sshRouteModeSchema,
    host: z.string(),
    port: z.number().int(),
    username: z.string(),
});
export const sshSessionSchema = z.object({
    sessionId: z.uuid(),
    nodeId: z.uuid(),
    nodeName: z.string(),
    /** Куда открывать WebSocket. Относительный путь — домен тот же (§3.1). */
    wsUrl: z.string(),
    /** Одноразовый, TTL 30 секунд (§10.1). */
    ticket: z.string(),
    expiresAt: z.iso.datetime(),
    capabilities: sshCapabilitiesSchema,
    server: sshServerInfoSchema,
});
/** TTL тикета на сессию, секунды (§10.1). Тот же, что у VNC. */
export const SSH_TICKET_TTL_SEC = 30;
/**
 * Предел одновременных сессий — общий с VNC.
 *
 * Считается по всем сессиям удалённого управления вместе: три вкладки VNC
 * плюс три терминала — это шесть открытых каналов к чужим машинам, и делить
 * их на два независимых счёта значило бы удваивать предел молча.
 */
export const SSH_DEFAULT_SESSION_LIMIT_PER_USER = 3;
export const SSH_DEFAULT_SESSION_LIMIT_PER_ORG = 20;
// — Управляющие сообщения терминала ————————————————————————
/**
 * Сообщения браузера шлюзу поверх того же WebSocket, что и байты терминала.
 *
 * Разделение простое: двоичный кадр — это ввод пользователя, текстовый —
 * управление. Заводить второе соединение ради изменения размера окна
 * было бы дороже во всех отношениях, а мешать управление с вводом в один
 * поток нельзя: в терминал набирают JSON не реже, чем всё остальное.
 */
export const sshClientMessageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('resize'),
        /** Размер в знакоместах, а не в пикселях: сервер меряет так. */
        cols: z.number().int().min(1).max(1000),
        rows: z.number().int().min(1).max(1000),
    }),
    z.object({ type: z.literal('ping') }),
]);
/**
 * Сообщения шлюза браузеру. Двоичный кадр — вывод сервера, текстовый —
 * состояние сессии.
 */
export const sshServerMessageSchema = z.discriminatedUnion('type', [
    /** Канал открыт, оболочка запущена — до этого момента терминал пуст. */
    z.object({ type: z.literal('ready'), server: sshServerInfoSchema }),
    z.object({ type: z.literal('pong'), at: z.number() }),
    /**
     * Оболочка завершилась сама: `exit` или `logout`.
     *
     * Не ошибка: сессия закрывается штатно, и терминал должен сказать
     * «соединение закрыто, код 0», а не предложить переподключиться.
     */
    z.object({
        type: z.literal('exit'),
        code: z.number().int().nullable(),
        signal: z.string().nullable(),
    }),
    z.object({
        type: z.literal('error'),
        code: z.string(),
        message: z.string(),
        /**
         * Подробности отказа.
         *
         * У смены ключа хоста здесь оба отпечатка — без них подтверждать
         * нечего: интерфейс показывает их рядом и спрашивает, тот ли это
         * сервер (ADR 026).
         */
        details: z.record(z.string(), z.unknown()).optional(),
    }),
]);
//# sourceMappingURL=ssh.js.map